import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

export const api: AxiosInstance = axios.create({
    baseURL: '/api',
    headers: {
        'Accept': 'application/json',
    },
    withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // CSRF token from the SPA shell's <meta name="csrf-token"> tag (R9)
        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrf) {
            config.headers['X-CSRF-TOKEN'] = csrf;
        }
        // Only set Content-Type to JSON for non-FormData payloads.
        // For FormData, let axios set multipart/form-data with the correct boundary.
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        } else {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
// NOTE: Do NOT unwrap `response.data` here. All call sites in hooks/stores/pages
// read the standard axios shape (`response.data`, `response.data.data`). Unwrapping
// broke every data-driven page (pricing plans, notifications, friends, videos, etc.).
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const expiresAt = localStorage.getItem('token_expires_at');
                if (expiresAt && Date.now() >= parseInt(expiresAt)) {
                    // Token expired, try to refresh
                    const response = await api.post('/v1/auth/refresh');
                    const { access_token: token, token_type, expires_at } = response.data;
                    localStorage.setItem('auth_token', token);
                    localStorage.setItem('auth_token_type', token_type);
                    if (expires_at) {
                        localStorage.setItem('token_expires_at', String(new Date(expires_at).getTime()));
                    }
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                }
            } catch {
                // Refresh failed, redirect to login
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_token_type');
                localStorage.removeItem('token_expires_at');
                window.location.href = '/login';
                return Promise.reject(error);
            }
        }

        // Handle 422 validation errors
        if (error.response?.status === 422) {
            const errors = (error.response.data as { errors: Record<string, string[]> }).errors;
            const firstError = Object.values(errors)[0] as string[];
            if (firstError?.[0]) {
                // Could trigger toast notification here
                console.error('Validation error:', firstError[0]);
            }
        }

        // Handle 403 forbidden
        if (error.response?.status === 403) {
            // Could trigger toast notification here
            console.error('Forbidden');
        }

        return Promise.reject(error);
    }
);

export default api;

// Export types
export type { AxiosInstance, AxiosError } from 'axios';
